import { type Dictionary } from '@ngrx/entity';
import { type WritableDraft } from 'immer';

import {
  type CanvasElement,
  type CanvasElementFrom,
  type CanvasElementId,
  type CanvasElementType,
  type ElementTargetPath,
  type PartialPropsOf,
  type SlotId,
} from '@blaze-runtime/engine-domain';
import { applyPropsPatch, getRootElementId } from '@blaze-runtime/engine-domain';
import {
  type CreateWorkflowRequest,
  LogicalType,
  QueryNodeType,
} from '@blaze-shared/api-contracts-domain';
import { type BaseSchema, type RuntimeEventType } from '@blaze-shared/schema-domain';
import { isEmptyObject } from '@blaze-shared/shared-util';
import {
  type Connection,
  findFirstNodeInWorkflowOfType,
  type GetWorkflowNode,
  isDatasetNode,
  isGetNode,
  type Workflow,
} from '@blaze-shared/workflow-domain';

import { type EngineState } from '../../+state/engine';
import { isElementType } from '../../canvas-elements/element-model/element-type-guards';
import { placeElementMutably } from '../../utils/layout';
import { getPathLastInSlot } from '../../utils/layout/helpers';
import {
  createConnection,
  getUiRuntimeElement,
  getWorkflowRuntimeElement,
} from '../../utils/runtime';
import {
  type ComponentWorkflows,
  type ElementContext,
  type ParentContext,
  type WorkflowContext,
} from '../models/ai.model';

import { type ComponentWorkflowsRequest, type PageLayout } from './page-layout.model';
import { processWorkflowInputs } from './workflow-with-inputs';

/**
 * Assigns the AI-provided named style ({@link BaseAiElementDefinition.styleName}) to
 * the element's `styleConfig` for any element type, unless a handler already set one.
 * Backend AI styles are theme-scoped and matched by name, so `styleName` is sufficient.
 */
function withAiStyleConfig<T extends CanvasElementType>(
  context: ElementContext<T>,
  props: PartialPropsOf<T>,
): PartialPropsOf<T> {
  const styleName = context.element.styleName;
  if (!styleName || (props as { styleConfig?: unknown }).styleConfig) {
    return props;
  }
  return applyPropsPatch(props, { styleConfig: { styleName } } as PartialPropsOf<T>);
}

export function createElement<T extends CanvasElementType>(
  draft: WritableDraft<EngineState>,
  context: ElementContext<T>,
  props: PartialPropsOf<T>,
  mergeWithInitialProps = true,
): { elementId: CanvasElementId } {
  props = withAiStyleConfig(context, props);
  const path = getPathForNewElement(draft, context);

  const result = placeElementMutably(draft, {
    elementDefinition: context.definition,
    atPath: path,
    initProps: mergeWithInitialProps ? applyPropsPatch(context.definition.initial, props) : props,
  });

  if (!result?.elementId) {
    throw new Error(`Element ${context.element.$type} was not created.`);
  }

  context.idsMapping[context.element.id] = result.elementId;

  return { elementId: result.elementId };
}

export function updateElement<T extends CanvasElementType>(
  element: WritableDraft<CanvasElementFrom<T>>,
  props: PartialPropsOf<T>,
): void {
  if ('props' in element) {
    element.props = applyPropsPatch<T>(element.props as PartialPropsOf<T>, props);
  }
}

export function upsertElement<T extends CanvasElementType>(
  draft: WritableDraft<EngineState>,
  context: ElementContext<T>,
  props: PartialPropsOf<T>,
  mergeWithInitialProps = true,
): { elementId: CanvasElementId } {
  const element = findElement<T>(draft, context.element.id);

  if (element) {
    updateElement(element, withAiStyleConfig(context, props));
    context.idsMapping[context.element.id] = element.id;
    return { elementId: element.id };
  } else {
    return createElement(draft, context, props, mergeWithInitialProps);
  }
}

export function getPathForNewElement<T extends CanvasElementType>(
  draft: WritableDraft<EngineState>,
  context: ElementContext<T>,
): ElementTargetPath {
  const isNewElementContainer = context.element.$type === 'container';
  const parentId = context.idsMapping[context.parent.id] ?? context.parent.id;
  const isParentRoot =
    isNewElementContainer && (parentId === 'root' || parentId === getRootElementId(context.pageId));

  const parent = isParentRoot ? getRoot(draft, context.pageId) : getContainerLike(draft, parentId);
  const slotId = getSlotId(parent, context.parent.slotId);
  const path = getPathLastInSlot(draft, parent.id, slotId);

  if (context.parent.index !== undefined && context.parent.index >= 0) {
    path.index = Math.min(context.parent.index, path.index ?? 0);
  }

  return path;
}

export function addWorkflowWithConnection<T extends CanvasElementType>(
  draft: WritableDraft<EngineState>,
  context: ElementContext<T>,
  workflow: Workflow,
): void {
  const elementId = context.idsMapping[context.element.id] ?? context.element.id;
  const connection = createConnection(
    context.pageId,
    getWorkflowRuntimeElement(workflow.id),
    getUiRuntimeElement(elementId),
    'data-ready',
  );

  draft.connections.push(connection);
  draft.workflows.push(workflow);
}

/**
 * Adds the AI-generated source workflow to the draft state if it is not already present
 * so the schema service can resolve `ExecuteWorkflow` references from bulk/row action
 * workflows that target it.
 *
 * The `data-ready` connection is intentionally NOT created here — `applyAiGeneratedWorkflow`
 * (dispatched from the handler's `finalize()`) creates that connection with a fresh ID.
 * Pre-adding a connection here would cause `applyAiGeneratedWorkflowMutably` to enter its
 * strip-and-readd branch and replace the connection.
 */
export function addSourceWorkflow(draft: WritableDraft<EngineState>, workflow: Workflow): void {
  if (draft.workflows.some((w) => w.id === workflow.id)) {
    return;
  }
  draft.workflows.push(workflow);
}

export function addEventWorkflowWithConnection<T extends CanvasElementType>(
  draft: WritableDraft<EngineState>,
  context: ElementContext<T>,
  workflow: Workflow,
  eventType: RuntimeEventType = 'action',
  supportedInputs?: Map<string, string>,
): { connection: Connection; hasUpdates: boolean } {
  const elementId = context.idsMapping[context.element.id] ?? context.element.id;
  const connection = createConnection(
    context.pageId,
    { id: elementId, layerType: 'Ui', output: 'events' },
    getWorkflowRuntimeElement(workflow.id),
    eventType,
  );

  const hasUpdates = processWorkflowInputs(
    workflow,
    connection,
    context.idsMapping,
    supportedInputs,
  );

  draft.connections.push(connection);
  draft.workflows.push(workflow);

  return { connection, hasUpdates };
}

export function createNewWorkflowRequest(
  datasetId: string,
  pageId: string,
  projectId: string,
): CreateWorkflowRequest {
  const node: GetWorkflowNode = {
    id: 'Get_0',
    nodeType: 'Get',
    datasetId,
    projectColumns: [],
    query: {
      nodeType: QueryNodeType.Logical,
      logicalType: LogicalType.And,
      children: [],
    },
    ignoreExternalAccountFiltering: false,
    isFormulaCondition: false,
    queryConditions: [],
    nextNodeId: null,
  };

  return {
    inputs: {},
    inputSchemas: [],
    isLongRunning: false,
    isServer: false,
    nodes: [node],
    pageId,
    projectId,
    startNodeId: node.id,
  };
}

export function getRoot(
  draft: WritableDraft<EngineState>,
  pageId: string,
): CanvasElementFrom<'root'> {
  for (const id of draft.ids) {
    const element = draft.entities[id];

    if (isElementType(element, 'root') && element.ns === pageId) {
      return element;
    }
  }

  throw new Error('Root element not found');
}

export function getContainerLike(
  draft: WritableDraft<EngineState>,
  elementId: CanvasElementId,
):
  | CanvasElementFrom<'container'>
  | CanvasElementFrom<'collapsibleContainer'>
  | CanvasElementFrom<'tabs'>
  | CanvasElementFrom<'formContainer'>
  | CanvasElementFrom<'formDetails'> {
  const element = draft.entities[elementId];

  if (
    isElementType(element, 'container') ||
    isElementType(element, 'collapsibleContainer') ||
    isElementType(element, 'tabs') ||
    isElementType(element, 'formContainer') ||
    isElementType(element, 'formDetails')
  ) {
    return element;
  }

  throw new Error(
    `Incorrect element. Expected container, collapsibleContainer, tabs, formContainer or formDetails, got ${element?.type}`,
  );
}

export function getSlotId(
  container:
    | CanvasElementFrom<'container'>
    | CanvasElementFrom<'collapsibleContainer'>
    | CanvasElementFrom<'tabs'>
    | CanvasElementFrom<'root'>
    | CanvasElementFrom<'formContainer'>
    | CanvasElementFrom<'formDetails'>,
  targetSlotId: SlotId | undefined,
): SlotId {
  if (targetSlotId && targetSlotId in container.slots.ids) {
    return targetSlotId;
  }

  return container.slots.ids[0];
}

export function getElement<T extends CanvasElementType>(
  draft: WritableDraft<EngineState>,
  elementId: CanvasElementId,
  type: T,
): CanvasElementFrom<T> {
  const element = findElement(draft, elementId);

  if (!isElementType(element, type)) {
    throw new Error(`Incorrect element. Expected ${type}, got ${element?.type}`);
  }

  return element;
}

export function findElement<T extends CanvasElementType>(
  draft: WritableDraft<EngineState>,
  elementId: CanvasElementId,
): WritableDraft<CanvasElementFrom<T>> | undefined {
  return draft.entities[draft.ai.elementsMap[elementId] ?? elementId] as
    | WritableDraft<CanvasElementFrom<T>>
    | undefined;
}

export function getSchemaForContextWithWorkflow(
  context: ElementContext<any> & { workflowContext?: WorkflowContext },
  draft: WritableDraft<EngineState>,
): BaseSchema | undefined {
  const datasetId = context.workflowContext
    ? findFirstNodeInWorkflowOfType(context.workflowContext.workflow, isGetNode)?.datasetId
    : undefined;
  return datasetId ? draft.schemas[datasetId] : undefined;
}

export function createParentContext(
  elementId: CanvasElementId,
  elements: Dictionary<CanvasElement>,
): ParentContext {
  const element = elements[elementId];

  if (!element) {
    throw new Error(`Element with id ${elementId} was not found`);
  }

  if (!element.parentNode) {
    throw new Error(`Element with id ${elementId} has no parent`);
  }

  const slot = element.parentNode.slot ? Number.parseInt(element.parentNode.slot, 10) : Number.NaN;
  const index = Number.isNaN(slot) ? undefined : slot;

  return {
    id: element.parentNode.id,
    index,
    slotId: element.parentNode.slot,
  };
}

export function findFirstContainerInRoot(
  elements: Dictionary<CanvasElement>,
  pageId: string,
): ParentContext {
  const root = Object.values(elements).find(
    (el): el is CanvasElementFrom<'root'> => isElementType(el, 'root') && el.ns === pageId,
  );

  if (!root) {
    throw new Error(`Root element for page ${pageId} was not found`);
  }

  for (const slotId of root.slots.ids) {
    for (const childId of root.slots[slotId]?.children ?? []) {
      const child = elements[childId];
      if (
        isElementType(child, 'container') ||
        isElementType(child, 'collapsibleContainer') ||
        isElementType(child, 'tabs')
      ) {
        return { id: childId };
      }
    }
  }

  throw new Error(`No container found in the root of page ${pageId} to place new components into`);
}

export function isAILayout(aiLayout: unknown): aiLayout is PageLayout {
  return !!(
    aiLayout &&
    typeof aiLayout === 'object' &&
    'page' in aiLayout &&
    aiLayout.page &&
    typeof aiLayout.page === 'object' &&
    'children' in aiLayout.page &&
    Array.isArray(aiLayout.page.children) &&
    'componentWorkflows' in aiLayout
  );
}

export function isAILayoutEmpty(aiLayout: PageLayout): boolean {
  return !aiLayout.page.children.length && isEmptyObject(aiLayout.componentWorkflows);
}

export function resolveComponentWorkflows(
  workflows: ComponentWorkflowsRequest,
): ComponentWorkflows {
  const resolved: ComponentWorkflows = {};
  for (const [key, entry] of Object.entries(workflows)) {
    if (entry?.workflow) {
      resolved[key] = {
        workflow: entry.workflow,
        inputVariables: entry.inputVariables ?? [],
      };
    }
  }
  return resolved;
}

export function collectWorkflowDatasetIds(workflow: Workflow): string[] {
  const ids = new Set<string>();
  for (const node of workflow.nodes ?? []) {
    if (isDatasetNode(node)) {
      const datasetId = node.datasetId;
      if (typeof datasetId === 'string' && datasetId) {
        ids.add(datasetId);
      }
    }
  }
  return [...ids];
}

export function bindValueToWorkflowSource<T extends CanvasElementType>(
  draft: WritableDraft<EngineState>,
  context: ElementContext<T> & { workflowContext?: WorkflowContext },
  workflow: Workflow,
): void {
  const elementId = context.idsMapping[context.element.id] ?? context.element.id;
  const element = findElement<T>(draft, elementId);
  if (!element || !('props' in element)) {
    return;
  }
  const schema = getSchemaForContextWithWorkflow(context, draft);
  const props = element.props as { data?: unknown[] };
  props.data = [
    ...((props.data ?? []) as { key: string }[]).filter((data) => data.key !== 'value'),
    {
      key: 'value',
      enabled: true,
      bindings: {},
      isCustomSelection: false,
      dataSource: {
        id: workflow.id,
        name: schema?.name ?? '',
        sourceType: 'context',
        columns: schema?.columns ?? [],
      },
    },
  ];
  addWorkflowWithConnection(draft, context, workflow);
}
